import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { veiculoService, colaboradorService } from '../../services/api';
import CRLVUpload from '../../components/CRLVUpload';

const Veiculos = () => {
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState({
    page: 1,
    limit: 10,
    tipo: '',
    status: 'ativo',
    marca: '',
    modelo: '',
    placa: ''
  });
  // const [buscaVeiculo, setBuscaVeiculo] = useState('');
  // const [mostrarDropdownVeiculos, setMostrarDropdownVeiculos] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [veiculoSelecionado, setVeiculoSelecionado] = useState(null);
  const [crlvData, setCrlvData] = useState(null);
  const [modalMotoristaOpen, setModalMotoristaOpen] = useState(false);
  const [veiculoParaMotorista, setVeiculoParaMotorista] = useState(null);
  const [motoristas, setMotoristas] = useState([]);
  const [carregandoMotoristas, setCarregandoMotoristas] = useState(false);
  const [formData, setFormData] = useState({
    placa: '',
    tipo: '',
    marca: '',
    modelo: '',
    ano: '',
    cor: '',
    capacidade: {
      passageiros: '',
      carga: ''
    },
    hodometro: {
      atual: ''
    },
    combustivel: {
      tipo: '',
      capacidade: '',
      nivelAtual: ''
    },
    documentos: {
      renavam: '',
      chassi: '',
      crlv: {
        numero: '',
        validade: ''
      },
      seguro: {
        numero: '',
        seguradora: '',
        validade: '',
        valor: ''
      },
      ipva: {
        ano: '',
        valor: '',
        vencimento: ''
      }
    },
    status: 'ativo',
    observacoes: ''
  });

  // Estados para gerenciar dados
  const [veiculosData, setVeiculosData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Garantir que temos um array válido para trabalhar
  // A API retorna { success: true, data: [...], pagination: {...} }
  // Então precisamos acessar veiculosData.data.data
  const veiculos = React.useMemo(() => {
    return Array.isArray(veiculosData?.data?.data) ? veiculosData.data.data : 
           Array.isArray(veiculosData?.data) ? veiculosData.data : [];
  }, [veiculosData]);
  
  // Função para carregar veículos
  const carregarVeiculos = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Filtrar parâmetros vazios
      const filtrosLimpos = Object.fromEntries(
        Object.entries(filtros).filter(([key, value]) => 
          value !== '' && value !== null && value !== undefined
        )
      );
      
      console.log('🚀 Carregando veículos...', filtrosLimpos);
      
      const response = await veiculoService.listar(filtrosLimpos);
      console.log('✅ Veículos carregados:', response);
      
      // Verificar se a resposta tem a estrutura esperada
      if (response && response.data) {
        setVeiculosData(response);
      } else {
        console.warn('⚠️ Resposta não tem estrutura esperada:', response);
        setVeiculosData({ data: [], pagination: {} });
      }
    } catch (err) {
      console.error('❌ Erro ao carregar veículos:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response,
        request: err.request
      });
      
      setError(err);
      
      // Mostrar erro mais específico
      if (err.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      } else if (err.response?.status === 500) {
        toast.error('Erro interno do servidor');
      } else {
        toast.error('Erro ao carregar veículos');
      }
    } finally {
      setIsLoading(false);
    }
  }, [filtros]);

  // Carregar veículos quando os filtros mudarem
  useEffect(() => {
    carregarVeiculos();
  }, [filtros, carregarVeiculos]);

  // Debug: verificar estrutura dos dados
  console.log('🔍 Veículos Data:', veiculosData);
  console.log('🔍 Veículos Data Type:', typeof veiculosData);
  console.log('🔍 Veículos Data Keys:', veiculosData ? Object.keys(veiculosData) : 'null');
  console.log('🔍 Veículos Array:', veiculosData?.data);
  console.log('🔍 Veículos Array Type:', typeof veiculosData?.data);
  console.log('🔍 Veículos Array IsArray:', Array.isArray(veiculosData?.data));
  console.log('🔍 Veículos Length:', veiculos.length);
  console.log('🔍 Filtros atuais:', filtros);
  console.log('🔍 Loading:', isLoading);
  console.log('🔍 Error:', error);
  console.log('🔍 Veículos para renderizar:', veiculos);

  // Função para criar veículo
  const criarVeiculo = async (dados) => {
    try {
      setIsLoading(true);
      await veiculoService.criar(dados);
      toast.success('Veículo criado com sucesso!');
      setModalOpen(false);
      resetForm();
      await carregarVeiculos(); // Recarregar lista
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar veículo');
    } finally {
      setIsLoading(false);
    }
  };

  // Função para atualizar veículo
  const atualizarVeiculo = async (id, dados) => {
    try {
      setIsLoading(true);
      await veiculoService.atualizar(id, dados);
      toast.success('Veículo atualizado com sucesso!');
      setModalOpen(false);
      resetForm();
      await carregarVeiculos(); // Recarregar lista
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar veículo');
    } finally {
      setIsLoading(false);
    }
  };

  // Função para deletar veículo
  const deletarVeiculo = async (id) => {
    try {
      setIsLoading(true);
      await veiculoService.deletar(id);
      toast.success('Veículo removido com sucesso!');
      await carregarVeiculos(); // Recarregar lista
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover veículo');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      placa: '',
      tipo: '',
      marca: '',
      modelo: '',
      ano: '',
      cor: '',
      capacidade: {
        passageiros: '',
        carga: ''
      },
      hodometro: {
        atual: ''
      },
      combustivel: {
        tipo: '',
        capacidade: '',
        nivelAtual: ''
      },
      documentos: {
        renavam: '',
        chassi: '',
        crlv: {
          numero: '',
          validade: ''
        },
        seguro: {
          numero: '',
          seguradora: '',
          validade: '',
          valor: ''
        },
        ipva: {
          ano: '',
          valor: '',
          vencimento: ''
        }
      },
      status: 'ativo',
      observacoes: ''
    });
    setVeiculoSelecionado(null);
  };

  const handleCreate = () => {
    setModalMode('create');
    resetForm();
    setModalOpen(true);
  };

  const handleEdit = (veiculo) => {
    console.log('🚀 ===== INÍCIO HANDLE EDIT =====');
    console.log('📝 Veículo selecionado:', veiculo);
    console.log('📝 documentos:', veiculo.documentos);
    console.log('📝 crlv:', veiculo.documentos?.crlv);
    console.log('📝 arquivo:', veiculo.documentos?.crlv?.arquivo);
    
    setModalMode('edit');
    setVeiculoSelecionado(veiculo);
    
    // Definir crlvData se existir arquivo válido
    if (veiculo.documentos?.crlv?.arquivo && veiculo.documentos.crlv.arquivo.nome) {
      console.log('✅ Definindo crlvData:', veiculo.documentos.crlv.arquivo);
      setCrlvData(veiculo.documentos.crlv.arquivo);
    } else {
      console.log('❌ Nenhum arquivo CRLV válido encontrado');
      setCrlvData(null);
    }
    console.log('🚀 ===== FIM HANDLE EDIT =====');
    setFormData({
      placa: veiculo.placa || '',
      tipo: veiculo.tipo || '',
      marca: veiculo.marca || '',
      modelo: veiculo.modelo || '',
      ano: veiculo.ano || '',
      cor: veiculo.cor || '',
      capacidade: {
        passageiros: veiculo.capacidade?.passageiros || '',
        carga: veiculo.capacidade?.carga || ''
      },
      hodometro: {
        atual: veiculo.hodometro?.atual || ''
      },
      combustivel: {
        tipo: veiculo.combustivel?.tipo || '',
        capacidade: veiculo.combustivel?.capacidade || '',
        nivelAtual: veiculo.combustivel?.nivelAtual || ''
      },
      documentos: {
        renavam: veiculo.documentos?.renavam || '',
        chassi: veiculo.documentos?.chassi || '',
        crlv: {
          numero: veiculo.documentos?.crlv?.numero || '',
          validade: veiculo.documentos?.crlv?.validade ? new Date(veiculo.documentos.crlv.validade).toISOString().split('T')[0] : ''
        },
        seguro: {
          numero: veiculo.documentos?.seguro?.numero || '',
          seguradora: veiculo.documentos?.seguro?.seguradora || '',
          validade: veiculo.documentos?.seguro?.validade ? new Date(veiculo.documentos.seguro.validade).toISOString().split('T')[0] : '',
          valor: veiculo.documentos?.seguro?.valor || ''
        },
        ipva: {
          ano: veiculo.documentos?.ipva?.ano || '',
          valor: veiculo.documentos?.ipva?.valor || '',
          vencimento: veiculo.documentos?.ipva?.vencimento ? new Date(veiculo.documentos.ipva.vencimento).toISOString().split('T')[0] : ''
        }
      },
      status: veiculo.status || 'ativo',
      observacoes: veiculo.observacoes || ''
    });
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja remover este veículo?')) {
      deletarVeiculo(id);
    }
  };

  // Consultar veiculo pela placa
  const consultarVeiculoPorPlaca = async (placa) => {
    if (!placa || placa.replace(/[^A-Za-z0-9]/g, "").length < 7) return;
    
    try {
      const response = await veiculoService.consultarPlaca(placa);
      const data = response.data;
      
      if (data.success && data.data) {
        // Veiculo ja existe no sistema
        const veiculo = data.data;
        setFormData({
          placa: veiculo.placa || "",
          tipo: veiculo.tipo || "",
          marca: veiculo.marca || "",
          modelo: veiculo.modelo || "",
          ano: veiculo.ano ? String(veiculo.ano) : "",
          cor: veiculo.cor || "",
          capacidade: {
            passageiros: veiculo.capacidade?.passageiros || "",
            carga: veiculo.capacidade?.carga || ""
          },
          hodometro: {
            atual: veiculo.hodometro?.atual || ""
          },
          combustivel: {
            tipo: veiculo.combustivel?.tipo || "",
            capacidade: veiculo.combustivel?.capacidade || "",
            nivelAtual: veiculo.combustivel?.nivelAtual || ""
          },
          documentos: {
            renavam: veiculo.documentos?.renavam || "",
            chassi: veiculo.documentos?.chassi || "",
            crlv: {
              numero: veiculo.documentos?.crlv?.numero || "",
              validade: veiculo.documentos?.crlv?.validade || ""
            },
            seguro: {
              numero: veiculo.documentos?.seguro?.numero || "",
              seguradora: veiculo.documentos?.seguro?.seguradora || "",
              validade: veiculo.documentos?.seguro?.validade || "",
              valor: veiculo.documentos?.seguro?.valor || ""
            },
            ipva: {
              ano: veiculo.documentos?.ipva?.ano || "",
              valor: veiculo.documentos?.ipva?.valor || "",
              vencimento: veiculo.documentos?.ipva?.vencimento || ""
            }
          },
          status: veiculo.status || "ativo",
          observacoes: veiculo.observacoes || ""
        });
        toast.success("Veiculo encontrado! Dados carregados: " + veiculo.marca + " " + veiculo.modelo + " (" + veiculo.placa + ")", { duration: 4000 });
      } else if (data.success && !data.data) {
        toast("Placa disponivel para cadastro", { icon: "info", duration: 3000 });
      }
    } catch (error) {
      console.error("Erro ao consultar placa:", error);
    }
  };

  // Abrir modal para vincular motorista
  const abrirVincularMotorista = (veiculo) => {
    setVeiculoParaMotorista(veiculo);
    setModalMotoristaOpen(true);
    setCarregandoMotoristas(true);
    colaboradorService.listar({ limit: 200, perfil: 'motorista' })
      .then(r => {
        const lista = Array.isArray(r.data?.data?.data) ? r.data.data.data :
                      Array.isArray(r.data?.data) ? r.data.data :
                      Array.isArray(r.data) ? r.data : [];
        setMotoristas(lista.filter(m => m.ativo !== false));
      })
      .catch(err => {
        console.error('Erro ao carregar motoristas:', err);
        toast.error('Erro ao carregar motoristas');
      })
      .finally(() => setCarregandoMotoristas(false));
  };

  // Vincular motorista ao veiculo
  const vincularMotorista = async (motoristaId) => {
    if (!veiculoParaMotorista) return;
    try {
      const response = await veiculoService.atualizar(veiculoParaMotorista._id, { motorista: motoristaId });
      if (response.data?.success) {
        toast.success('Motorista vinculado com sucesso!');
        setModalMotoristaOpen(false);
        setVeiculoParaMotorista(null);
        // Recarregar lista
        setVeiculosData(null);
        carregarVeiculos();
      } else {
        toast.error(response.data?.message || 'Erro ao vincular motorista');
      }
    } catch (error) {
      console.error('Erro ao vincular motorista:', error);
      toast.error(error.response?.data?.message || 'Erro ao vincular motorista');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const dados = {
      ...formData,
      ano: parseInt(formData.ano),
      capacidade: {
        passageiros: formData.capacidade.passageiros ? parseInt(formData.capacidade.passageiros) : undefined,
        carga: formData.capacidade.carga ? parseInt(formData.capacidade.carga) : undefined
      },
      hodometro: {
        atual: parseInt(formData.hodometro.atual)
      },
      combustivel: {
        tipo: formData.combustivel.tipo,
        capacidade: formData.combustivel.capacidade ? parseInt(formData.combustivel.capacidade) : undefined,
        nivelAtual: formData.combustivel.nivelAtual ? parseInt(formData.combustivel.nivelAtual) : undefined
      },
      documentos: {
        ...formData.documentos,
        crlv: {
          numero: formData.documentos.crlv.numero,
          validade: formData.documentos.crlv.validade ? new Date(formData.documentos.crlv.validade) : undefined
        },
        seguro: {
          ...formData.documentos.seguro,
          validade: formData.documentos.seguro.validade ? new Date(formData.documentos.seguro.validade) : undefined,
          valor: formData.documentos.seguro.valor ? parseFloat(formData.documentos.seguro.valor) : undefined
        },
        ipva: {
          ano: formData.documentos.ipva.ano ? parseInt(formData.documentos.ipva.ano) : undefined,
          valor: formData.documentos.ipva.valor ? parseFloat(formData.documentos.ipva.valor) : undefined,
          vencimento: formData.documentos.ipva.vencimento ? new Date(formData.documentos.ipva.vencimento) : undefined
        }
      }
    };

    if (modalMode === 'create') {
      criarVeiculo(dados);
    } else {
      atualizarVeiculo(veiculoSelecionado._id, dados);
    }
  };

  const handlePageChange = (newPage) => {
    setFiltros(prev => ({ ...prev, page: newPage }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativo': return 'bg-green-100 text-green-800';
      case 'manutencao': return 'bg-yellow-100 text-yellow-800';
      case 'inativo': return 'bg-gray-100 text-gray-800';
      case 'acidente': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      tcb: 'TCB',
      van: 'Van',
      onibus: 'Ônibus',
      caminhao: 'Caminhão',
      moto: 'Moto',
      outro: 'Outro'
    };
    return tipos[tipo] || tipo;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-semibold mb-2">Erro ao carregar veículos</div>
          <div className="text-gray-600 mb-4">{error.message}</div>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 rounded-2xl p-6 md:p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-indigo-300 text-sm font-medium uppercase tracking-widest mb-1">Frota</p>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Veículos</h1>
            <p className="text-indigo-200 mt-1 text-sm">Gerencie a frota de veículos</p>
          </div>
          <div className="flex space-x-2">

            <button
              onClick={() => {
                console.log('🔄 Forçando refresh...');
                carregarVeiculos();
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
            >
              🔄 Refresh
            </button>
            <button
              onClick={handleCreate}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center"
            >
              <span className="mr-2">🚗</span>
              Novo Veículo
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={filtros.tipo}
              onChange={(e) => setFiltros(prev => ({ ...prev, tipo: e.target.value, page: 1 }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todos os tipos</option>
              <option value="tcb">TCB</option>
              <option value="van">Van</option>
              <option value="onibus">Ônibus</option>
              <option value="caminhao">Caminhão</option>
              <option value="moto">Moto</option>
              <option value="outro">Outro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filtros.status}
              onChange={(e) => setFiltros(prev => ({ ...prev, status: e.target.value, page: 1 }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todos os status</option>
              <option value="ativo">Ativo</option>
              <option value="manutencao">Manutenção</option>
              <option value="inativo">Inativo</option>
              <option value="acidente">Acidente</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
            <input
              type="text"
              value={filtros.marca}
              onChange={(e) => setFiltros(prev => ({ ...prev, marca: e.target.value, page: 1 }))}
              placeholder="Digite a marca"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Placa</label>
            <input
              type="text"
              value={filtros.placa}
              onChange={(e) => setFiltros(prev => ({ ...prev, placa: e.target.value, page: 1 }))}
              placeholder="Digite a placa"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Tabela de Veículos */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Veículo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hodômetro
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Motorista
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CRLV
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {veiculos.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    Nenhum veículo encontrado
                  </td>
                </tr>
              ) : (
                veiculos.map((veiculo) => (
                  <tr key={veiculo._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {veiculo.placa}
                        </div>
                        <div className="text-sm text-gray-500">
                          {veiculo.marca} {veiculo.modelo} ({veiculo.ano})
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {getTipoLabel(veiculo.tipo)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(veiculo.status)}`}>
                        {veiculo.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {veiculo.hodometro?.atual?.toLocaleString()} km
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {veiculo.motorista?.nome || (
                        <button
                          onClick={() => abrirVincularMotorista(veiculo)}
                          className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 transition"
                        >
                          + Vincular motorista
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {veiculo.documentos?.crlv?.arquivo ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                            Disponível
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-start gap-1.5">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            <span className="w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                            Não enviado
                          </span>
                          <button
                            onClick={() => handleEdit(veiculo)}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-100 transition"
                          >
                            ⬆ Carregar CRLV
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/frota/veiculos/${veiculo._id}/historico-pontos`)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Histórico
                        </button>
                        <button
                          onClick={() => navigate(`/frota/veiculos/${veiculo._id}/manutencoes`)}
                          className="text-purple-600 hover:text-purple-900"
                        >
                          Manutenções
                        </button>
                        <button
                          onClick={() => handleEdit(veiculo)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(veiculo._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remover
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {veiculosData?.data?.pagination && veiculosData.data.pagination.total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(filtros.page - 1)}
                disabled={!veiculosData.data.pagination.hasPrevPage}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => handlePageChange(filtros.page + 1)}
                disabled={!veiculosData.data.pagination.hasNextPage}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Próximo
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando <span className="font-medium">{((filtros.page - 1) * filtros.limit) + 1}</span> a{' '}
                  <span className="font-medium">
                    {Math.min(filtros.page * filtros.limit, veiculosData.data.pagination.total)}
                  </span> de{' '}
                  <span className="font-medium">{veiculosData.data.pagination.total}</span> resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(filtros.page - 1)}
                    disabled={!veiculosData.data.pagination.hasPrevPage}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    ←
                  </button>
                  {Array.from({ length: veiculosData.data.pagination.totalPages }, (_, i) => i + 1)
                    .filter(page =>
                      page === 1 ||
                      page === veiculosData.data.pagination.totalPages ||
                      Math.abs(page - filtros.page) <= 2
                    )
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        )}
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === filtros.page
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}
                  <button
                    onClick={() => handlePageChange(filtros.page + 1)}
                    disabled={!veiculosData.data.pagination.hasNextPage}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    →
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Formulário */}
      {modalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalMode === 'create' ? 'Novo Veículo' : 'Editar Veículo'}
                </h3>
                <button
                  onClick={() => setModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Fechar</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Informações Básicas */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">Informações Básicas</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Placa *</label>
                      <input
                        type="text"
                        value={formData.placa}
                        onChange={(e) => setFormData(prev => ({ ...prev, placa: e.target.value.toUpperCase() }))}
                        onBlur={(e) => consultarVeiculoPorPlaca(e.target.value)}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="ABC-1234"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                      <select
                        value={formData.tipo}
                        onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Selecione o tipo</option>
                        <option value="tcb">TCB</option>
                        <option value="van">Van</option>
                        <option value="onibus">Ônibus</option>
                        <option value="caminhao">Caminhão</option>
                        <option value="moto">Moto</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Marca *</label>
                      <input
                        type="text"
                        value={formData.marca}
                        onChange={(e) => setFormData(prev => ({ ...prev, marca: e.target.value }))}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: Mercedes, Volkswagen"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Modelo *</label>
                      <input
                        type="text"
                        value={formData.modelo}
                        onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: Sprinter, Crafter"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ano *</label>
                      <input
                        type="number"
                        value={formData.ano}
                        onChange={(e) => setFormData(prev => ({ ...prev, ano: e.target.value }))}
                        required
                        min="1900"
                        max={new Date().getFullYear() + 1}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cor</label>
                      <input
                        type="text"
                        value={formData.cor}
                        onChange={(e) => setFormData(prev => ({ ...prev, cor: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: Branco, Azul"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="ativo">Ativo</option>
                        <option value="manutencao">Manutenção</option>
                        <option value="inativo">Inativo</option>
                        <option value="acidente">Acidente</option>
                      </select>
                    </div>
                  </div>

                  {/* Capacidade e Hodômetro */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">Capacidade e Hodômetro</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Capacidade de Passageiros</label>
                      <input
                        type="number"
                        value={formData.capacidade.passageiros}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          capacidade: { ...prev.capacidade, passageiros: e.target.value }
                        }))}
                        min="1"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: 20"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Capacidade de Carga (kg)</label>
                      <input
                        type="number"
                        value={formData.capacidade.carga}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          capacidade: { ...prev.capacidade, carga: e.target.value }
                        }))}
                        min="0"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: 1000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hodômetro Atual (km) *</label>
                      <input
                        type="number"
                        value={formData.hodometro.atual}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          hodometro: { ...prev.hodometro, atual: e.target.value }
                        }))}
                        required
                        min="0"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: 50000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Combustível</label>
                      <select
                        value={formData.combustivel.tipo}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          combustivel: { ...prev.combustivel, tipo: e.target.value }
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Selecione o combustível</option>
                        <option value="gasolina">Gasolina</option>
                        <option value="etanol">Etanol</option>
                        <option value="diesel">Diesel</option>
                        <option value="flex">Flex</option>
                        <option value="eletrico">Elétrico</option>
                        <option value="hibrido">Híbrido</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Capacidade do Tanque (L)</label>
                      <input
                        type="number"
                        value={formData.combustivel.capacidade}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          combustivel: { ...prev.combustivel, capacidade: e.target.value }
                        }))}
                        min="0"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: 80"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nível Atual do Tanque (%)</label>
                      <input
                        type="number"
                        value={formData.combustivel.nivelAtual}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          combustivel: { ...prev.combustivel, nivelAtual: e.target.value }
                        }))}
                        min="0"
                        max="100"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: 75"
                      />
                    </div>
                  </div>
                </div>

                {/* Documentos */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Documentos</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">RENAVAM</label>
                      <input
                        type="text"
                        value={formData.documentos.renavam}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          documentos: { ...prev.documentos, renavam: e.target.value }
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: 12345678901"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Chassi</label>
                      <input
                        type="text"
                        value={formData.documentos.chassi}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          documentos: { ...prev.documentos, chassi: e.target.value }
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: 9BWZZZZZZZZ123456"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Número do CRLV</label>
                      <input
                        type="text"
                        value={formData.documentos.crlv.numero}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          documentos: { 
                            ...prev.documentos, 
                            crlv: { ...prev.documentos.crlv, numero: e.target.value }
                          }
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: 123456789"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Validade do CRLV</label>
                      <input
                        type="date"
                        value={formData.documentos.crlv.validade}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          documentos: { 
                            ...prev.documentos, 
                            crlv: { ...prev.documentos.crlv, validade: e.target.value }
                          }
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  {/* Upload do CRLV */}
                  <div className="mt-4">
                    <CRLVUpload 
                      veiculoId={veiculoSelecionado?._id}
                      crlvData={crlvData}
                      onUpdate={(arquivo) => {
                        console.log('🚀 ===== INÍCIO ONUPDATE =====');
                        console.log('🔄 Arquivo recebido no onUpdate:', arquivo);
                        console.log('🔄 Tipo do arquivo:', typeof arquivo);
                        console.log('🔄 Arquivo tem nome?', arquivo?.nome);
                        
                        setCrlvData(arquivo);
                        
                        if (veiculoSelecionado) {
                          console.log('🔄 Atualizando veiculoSelecionado...');
                          setVeiculoSelecionado(prev => ({
                            ...prev,
                            documentos: {
                              ...prev.documentos,
                              crlv: {
                                ...prev.documentos.crlv,
                                arquivo: arquivo
                              }
                            }
                          }));
                          console.log('✅ veiculoSelecionado atualizado');
                        } else {
                          console.log('⚠️ veiculoSelecionado não existe');
                        }
                        console.log('🚀 ===== FIM ONUPDATE =====');
                      }}
                    />
                  </div>
                </div>

                {/* Observações */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Observações adicionais sobre o veículo..."
                  />
                </div>

                {/* Botões */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {isLoading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Vincular Motorista */}
      {modalMotoristaOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
             onClick={() => setModalMotoristaOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-indigo-950 to-indigo-900 rounded-t-2xl">
              <div>
                <h3 className="text-lg font-bold text-white">Vincular Motorista</h3>
                <p className="text-sm text-indigo-300">
                  {veiculoParaMotorista?.placa} - {veiculoParaMotorista?.modelo}
                </p>
              </div>
              <button onClick={() => setModalMotoristaOpen(false)} className="text-white hover:text-gray-300 text-2xl font-bold">×</button>
            </div>

            <div className="p-6">
              {carregandoMotoristas ? (
                <div className="py-10 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent mx-auto"></div>
                  <p className="text-gray-500 mt-3">Carregando motoristas...</p>
                </div>
              ) : motoristas.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-gray-500">Nenhum motorista disponível</p>
                  <p className="text-sm text-gray-400 mt-1">Cadastre colaboradores com perfil motorista</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {motoristas.map(m => (
                    <div key={m._id} className="flex items-center justify-between p-3 border border-gray-200 rounded-xl hover:bg-indigo-50/50 transition">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                          {m.nome?.split(' ').map(p => p[0]).slice(0, 2).join('')}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{m.nome}</p>
                          <p className="text-xs text-gray-500">{m.matricula} · {m.veiculoVinculado ? 'Possui veículo' : 'Sem veículo'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => vincularMotorista(m._id)}
                        disabled={!!m.veiculoVinculado}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                          m.veiculoVinculado
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700'
                        }`}
                      >
                        {m.veiculoVinculado ? 'Vinculado' : 'Vincular'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Veiculos;
